import { computed, onBeforeUnmount, onMounted } from 'vue'

import { useHead } from 'nuxt/app'

import { useLocationStore } from '@common/location-layer/store/location.store'

/** Fallback when the portal payload has no portalBannerText. */
const PLATFORM_FALLBACK = 'Platform'

/** Title separator. Single source of truth — change here to swap the divider. */
const TITLE_SEPARATOR = '|'

/**
 * useBrandedHead — clientportal-only.
 *
 * Wires Vue head (`useHead`) to the shared portal-settings store so the SSR
 * HTML carries the right favicon link and a `{Page} | {Portal}` title on the
 * first paint.
 *
 * Uses the function form of `titleTemplate` rather than the unhead string
 * template (`'%s %separator %siteName'`). Reason: this project is on
 * Nuxt 3.21.2 + `@unhead/vue@2.1.13`, and the v2 templateParams interpolation
 * is gated behind an opt-in plugin that is not registered in Nuxt's default
 * head config. Without the plugin the rendered title contains the literal
 * `%separator` and `%siteName` tokens (verified in the browser). The function
 * form is a first-class unhead API that needs no plugin.
 *
 * Page-first ordering (`{Page} | {Portal}`) follows the SEO / UX convention
 * used by every major SaaS — browser tabs truncate from the right, so the
 * page identifier keeps prime real estate, and search-result snippets surface
 * the page name before the brand.
 *
 * If a page does not call `useHead({ title })`, the template returns the
 * portal name alone (no dangling separator).
 *
 * On mount, flips `store.brandedHeadOwnsDocument = true` so the legacy
 * imperative writes inside `applyBranding` no-op on clientportal — Vue head is
 * the single writer here. On unmount the flag is cleared.
 *
 * Do not import from gokollab or meet — those apps deliberately keep the
 * legacy imperative path in `applyBranding`.
 */
export function useBrandedHead(): void {
  const store = useLocationStore()

  const siteName = computed<string>(() => store.branding.portalBannerText || store.locationData?.portalBannerText || PLATFORM_FALLBACK)

  const favicon = computed<string | null>(() => store.branding.favicon || store.locationData?.branding?.favicon || null)

  useHead({
    titleTemplate: (chunk?: string | null) => {
      const site = siteName.value
      if (!chunk) return site
      return `${chunk} ${TITLE_SEPARATOR} ${site}`
    },
    link: () => {
      const href = favicon.value
      return href ? [{ rel: 'icon', type: 'image/x-icon', href }] : []
    },
  })

  onMounted(() => {
    store.brandedHeadOwnsDocument = true
  })
  onBeforeUnmount(() => {
    store.brandedHeadOwnsDocument = false
  })
}
